import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface RawVideoItem {
  title: string;
  videos: { url: string; title: string }[];
  icon?: string;
}

interface VideoItem {
  title: string;
  url: string;
  duration?: number;
  watched?: boolean;
  progress?: number;
  icon?: string;
}

@Component({
  selector: 'app-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
})
export class VideoPlayerComponent implements AfterViewInit, OnChanges {
  @ViewChild('videoPlayer', { static: false }) videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('progressContainer', { static: false }) progressRef!: ElementRef<HTMLDivElement>;
  @ViewChild('playerContainer', { static: false }) playerContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('playlistScroll', { static: false }) playlistScrollRef!: ElementRef<HTMLDivElement>;
  @ViewChildren('playlistItem', { read: ElementRef }) playlistItems!: QueryList<ElementRef>;

  @Input() incomingData: RawVideoItem[] = [];

  constructor(private cdr: ChangeDetectorRef, private sanitizer: DomSanitizer,) {}

  safeSvg(svg: string): SafeHtml {
      return this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  // ----- readiness -----
  private viewReady = false;
  private pendingLoadIndex: number | null = null;

  loading = true;

  // ----- forward/back limits -----
  canForward = false;
  private forwardAnchor: number | null = null;
  private static readonly EPS = 0.25;
  private static readonly COMPLETE_THRESHOLD = 0.99;

  // ----- playlist & timing -----
  playlist: VideoItem[] = [];
  currentIndex = 0;
  totalPlaylistDuration = 0;
  completedVideosDuration = 0;

  // ----- player state -----
  isPaused = true;
  currentTime = 0;
  duration = 0;

  shouldPlayAfterLoad = false;
  maxWatchedTime = 0;
  maxReachedIndex = 0;
  userHasInteracted = false;

  // ---------- lifecycle ----------
  ngAfterViewInit(): void {
    this.viewReady = true;
    

    if (this.incomingData?.length) {
      void this.processData();
    }

    if (this.pendingLoadIndex !== null) {
      const idx = this.pendingLoadIndex;
      this.pendingLoadIndex = null;
      this.loadVideo(idx);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['incomingData']?.currentValue) {
      if (this.viewReady) {
        void this.processData();
      }
      // else ngAfterViewInit will process
    }
  }

  // ---------- safe element getter ----------
  private get el(): HTMLVideoElement | null {
    return this.videoRef?.nativeElement ?? null;
  }

  // ---------- computed ----------
  get progressPercent(): number {
    return this.duration ? (this.currentTime / this.duration) * 100 : 0;
  }
  get watchedPercent(): number {
    return this.duration ? (this.maxWatchedTime / this.duration) * 100 : 0;
  }
  get totalElapsed(): number {
    return this.completedVideosDuration + this.currentTime;
  }
  get totalRemaining(): number {
    return Math.max(0, this.totalPlaylistDuration - this.totalElapsed);
  }

  // ---------- controls ----------
  onTogglePlayPause(): void {
    if (!this.el) return;

    this.userHasInteracted = true;
    this.ensureFirstVideoReady();

    if (this.shouldPlayAfterLoad) {
      this.shouldPlayAfterLoad = false;
      this.el.muted = true;
      this.el
        .play()
        .then(() => {
          this.isPaused = false;
        })
        .catch((err) => {
          console.error('Play error:', err);
          this.isPaused = true;
          this.shouldPlayAfterLoad = true;
        });
      return;
    }

    if (this.el.paused) {
      this.el
        .play()
        .then(() => (this.isPaused = false))
        .catch((err) => console.error('Play error:', err));
    } else {
      this.el.pause();
    }
  }

  private scrollActiveIntoView(): void {
    if (!this.playlistItems) return;
    const items = this.playlistItems.toArray();
    const active = items[this.currentIndex];
    if (active) {
      active.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }

  onSeek(ev: MouseEvent): void {
    if (!this.duration || !this.el) return;

    const el = ev.currentTarget as HTMLElement;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const ratio = Math.min(1, Math.max(0, x / rect.width));
    const seekTime = ratio * this.duration;
    const now = this.currentTime || 0;

    if (seekTime + VideoPlayerComponent.EPS < now) {
      // BACKWARD: remember where we left so forward can restore to here
      this.forwardAnchor = now;
      this.canForward = true;

      this.el.currentTime = seekTime;
      this.currentTime = seekTime;
      return;
    }

    // FORWARD: allow only up to the forwardAnchor (if set) otherwise up to watched region
    const forwardLimit = this.forwardAnchor ?? this.maxWatchedTime;
    if (seekTime <= forwardLimit + VideoPlayerComponent.EPS) {
      this.el.currentTime = seekTime;
      this.currentTime = seekTime;

      // reached/passed the anchor → disable forward again
      if (
        this.forwardAnchor !== null &&
        seekTime >= this.forwardAnchor - VideoPlayerComponent.EPS
      ) {
        this.canForward = false;
        this.forwardAnchor = null;
      }
    }
  }

  onFullScreenToggle(): void {
    const container = this.playerContainerRef?.nativeElement as
      | HTMLElement
      | undefined;
    if (!container) return;

    if (!document.fullscreenElement) {
      container
        .requestFullscreen()
        .catch((err) => alert(`Error enabling full screen: ${err.message}`));
    } else {
      document.exitFullscreen();
    }
  }

  onPickFromPlaylist(idx: number): void {
    if (idx <= this.maxReachedIndex) {
      this.userHasInteracted = true;
      this.shouldPlayAfterLoad = true;
      this.loadVideo(idx);
    }
  }

  // ---------- player events ----------
  onPlay(): void {
    this.isPaused = false;
  }
  onPause(): void {
    this.isPaused = true;
  }

  onLoadedMetadata(): void {
    if (!this.el) return;
    this.duration = this.el.duration || 0;

    // write current row duration if missing
    const cur = this.playlist[this.currentIndex];
    if (cur && (!cur.duration || cur.duration === 0)) {
      cur.duration = this.duration;
      this.totalPlaylistDuration = this.playlist.reduce(
        (s, v) => s + (v.duration || 0),
        0
      );
      this.cdr.detectChanges();
    }
  }

  onTimeUpdate(): void {
    if (!this.el) return;
    this.currentTime = this.el.currentTime;
    this.maxWatchedTime = Math.max(this.maxWatchedTime, this.currentTime);

    // forward control cleanup
    if (
      this.forwardAnchor !== null &&
      this.currentTime >= this.forwardAnchor - VideoPlayerComponent.EPS
    ) {
      this.canForward = false;
      this.forwardAnchor = null;
    }

    // --- NEW: track progress / watched
    const cur = this.playlist[this.currentIndex];
    if (cur && this.duration > 0) {
      const p = this.currentTime / this.duration;
      cur.progress = Math.max(0, Math.min(1, p));
      if (!cur.watched && p >= VideoPlayerComponent.COMPLETE_THRESHOLD) {
        cur.watched = true;
        this.cdr.markForCheck();
      }
    }
  }

  onCanPlay(): void {
    if (!this.el) return;
    // if first attempt was blocked, try again when media can play
    if (this.shouldPlayAfterLoad) {
      this.shouldPlayAfterLoad = false;
      this.el.play().catch((e) => console.error('Autoplay error:', e));
    }
  }

  onEnded(): void {
    this.canForward = false;
    this.forwardAnchor = null;

    const cur = this.playlist[this.currentIndex];
    const curDur = cur?.duration ?? this.duration ?? 0;

    // mark watched on end
    if (cur) {
      cur.progress = 1;
      cur.watched = true;
    }

    this.completedVideosDuration += curDur;

    const next = this.currentIndex + 1;

    if (next < this.playlist.length) {
      // continue normally
      this.maxReachedIndex = Math.max(this.maxReachedIndex, next);
      this.shouldPlayAfterLoad = true;
      this.loadVideo(next);
      setTimeout(() => this.scrollActiveIntoView(), 0);
    } else {
      // last video finished — clamp totals and pause
      this.completedVideosDuration = this.totalPlaylistDuration;
      this.currentTime = 0;
      this.isPaused = true;
    }
  }

  onError(): void {
    console.error(
      `Failed to load video: "${this.playlist[this.currentIndex]?.title}"`
    );
    const next = this.currentIndex + 1;

    if (next < this.playlist.length) {
      this.shouldPlayAfterLoad = true;
      this.loadVideo(next);
    } else {
      this.completedVideosDuration = Math.min(
        this.completedVideosDuration,
        this.totalPlaylistDuration
      );
      this.currentTime = 0;
      this.isPaused = true;
    }
  }

  stepBack(seconds: number = 10): void {
    if (!this.el) return;
    const now = this.el.currentTime || 0;
    // mark where we left so forward can restore to here
    this.forwardAnchor = now;
    this.canForward = true;

    const newTime = Math.max(0, now - seconds);
    this.el.currentTime = newTime;
    this.currentTime = newTime;
  }

  stepForward(seconds: number = 10): void {
    if (!this.el || !this.canForward) return;

    const limit = (this.forwardAnchor ?? this.maxWatchedTime) || 0;
    const now = this.el.currentTime || 0;

    // only allow up to the anchor (never past)
    const target = Math.min(now + seconds, limit);
    if (target <= now + VideoPlayerComponent.EPS) {
      // we're at/over the limit; disable forward
      this.canForward = false;
      this.forwardAnchor = null;
      return;
    }

    this.el.currentTime = target;
    this.currentTime = target;

    // if we reached the anchor, disable forward again
    if (
      this.forwardAnchor !== null &&
      target >= this.forwardAnchor - VideoPlayerComponent.EPS
    ) {
      this.canForward = false;
      this.forwardAnchor = null;
    }
  }

  // ---------- data / durations ----------
  private async processData(): Promise<void> {
  try {
    this.loading = true;

    // Build VideoItem[] in original order, no nulls
    const items: VideoItem[] = [];
    for (const item of (this.incomingData ?? [])) {
      const first = item.videos?.[0];
      if (!first?.url) continue;

      items.push({
        title: first.title || item.title,
        url: this.sanitizeUrl(first.url.trim()),
        icon: item.icon ? item.icon : undefined,
      });
    }

    this.playlist = items;

    if (!this.playlist.length) {
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    this.loadVideo(0);

    this.loading = false;
    this.cdr.detectChanges();
    

    // durations in background
    setTimeout(async () => {
      try {
        await this.calculateTotalDuration();
        this.cdr.detectChanges();
      } catch {}
    }, 0);

  } catch (e) {
    console.error('Error processing video data:', e);
  } finally {
    if (this.loading) {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }
}



  private ensureFirstVideoReady(): void {
    if (!this.playlist.length) return;

    if (!this.el) {
      this.pendingLoadIndex = 0;
      return;
    }
    if (!this.el.src) this.loadVideo(0);
  }

  private async calculateTotalDuration(): Promise<void> {
    const durationPromises = this.playlist.map((v) =>
      this.getVideoDuration(v.url).catch(() => 0)
    );
    const durations = await Promise.all(durationPromises);

    let total = 0;
    this.playlist = this.playlist.map((v, i) => {
      const d = durations[i] ?? 0;
      total += d;
      return { ...v, duration: d };
    });
    this.totalPlaylistDuration = total;
  }

  private getVideoDuration(url: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const temp = document.createElement('video');
      temp.preload = 'metadata';
      temp.crossOrigin = 'anonymous';
      const cleanup = () => {
        temp.removeAttribute('src');
        temp.load();
      };
      temp.onloadedmetadata = () => {
        const d = temp.duration;
        cleanup();
        resolve(isFinite(d) ? d : 0);
      };
      temp.onerror = () => {
        cleanup();
        reject(new Error(`metadata load failed for ${url}`));
      };
      temp.src = url;
    });
  }

  private loadVideo(index: number): void {
    if (index > this.maxReachedIndex && index !== 0) return;

    if (!this.viewReady || !this.el) {
      this.pendingLoadIndex = index;
      return;
    }

    // reset forward control for new video
    this.canForward = false;
    this.forwardAnchor = null;

    this.currentIndex = index;
    this.maxWatchedTime = 0;
    this.currentTime = 0;
    this.duration = 0;

    this.completedVideosDuration = this.playlist
      .slice(0, index)
      .reduce((sum, v) => sum + (v.duration || 0), 0);

    const item = this.playlist[index];
    if (item) {
      // autoplay-friendly setup
      this.el.muted = true;
      this.el.setAttribute('muted', '');
      this.el.setAttribute('playsinline', '');
      this.el.setAttribute('autoplay', '');
      this.el.preload = 'auto';

      this.el.src = item.url;
      this.el.load();

      const p = this.el.play();
      if (p && typeof p.then === 'function') {
        p.then(() => (this.isPaused = false)).catch(() => {
          this.shouldPlayAfterLoad = true;
          this.isPaused = true;
        });
      }
    }

    setTimeout(() => this.scrollActiveIntoView(), 0);
  }

  private playNextVideo(): void {
    const next = this.currentIndex + 1;
    if (next < this.playlist.length) {
      this.maxReachedIndex = Math.max(this.maxReachedIndex, next);
      this.shouldPlayAfterLoad = true;
      this.loadVideo(next);
      setTimeout(() => this.scrollActiveIntoView(), 0);
    }
  }

  // ---------- utils ----------
  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds) || seconds < 0) return '00:00';
    const hh = Math.floor(seconds / 3600);
    const mm = Math.floor((seconds % 3600) / 60);
    const ss = Math.floor(seconds % 60);
    const mmStr = String(mm).padStart(2, '0');
    const ssStr = String(ss).padStart(2, '0');
    return hh > 0 ? `${String(hh).padStart(2, '0')}:${mmStr}:${ssStr}` : `${mmStr}:${ssStr}`;
  }

  private sanitizeUrl(url: string): string {
    try {
      const decoded = decodeURIComponent(url.replace(/\+/g, ' '));
      return encodeURI(decoded);
    } catch {
      return url;
    }
  }
}
